import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Plus, PencilSimple, Trash, FloppyDisk, X, Warning, FilePdf, CircleNotch, Sparkle, Info, Clock, ArrowClockwise, EyeSlash, Storefront, Scroll, ChatCircleText, Fingerprint, Books, Brain } from "@phosphor-icons/react";
import KnowledgeDiagnostic from "./KnowledgeDiagnostic";
import KnowledgeV2Panel from "./KnowledgeV2Panel";

const CATEGORIES = [
  { value: "policy", label: "Política" },
  { value: "culture", label: "Cultura" },
  { value: "objection", label: "Objeção" },
  { value: "procedure", label: "Procedimento" },
  { value: "faq", label: "FAQ" },
  { value: "operator_relay", label: "Aprendido com o operador" },
];

const empty = { title: "", content: "", category: "policy", tags: [], active: true };

function expiryStatus(d) {
  if (d.expired_at) return { tone: "rose", label: "Expirado" };
  if (!d.expires_at) return null;
  const ms = new Date(d.expires_at).getTime() - Date.now();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (ms <= 0) return { tone: "rose", label: "Expirado" };
  if (days <= 7) return { tone: "amber", label: `Expira em ${days}d` };
  return { tone: "slate", label: `Válido por ${days}d` };
}

export default function KnowledgeTab({ onChange, onToast }) {
  const [items, setItems] = useState([]);
  const [review, setReview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // v0.99.13 · Feb/2026 — Toggle v1 / v2. Persiste em localStorage
  // pra o operador não perder a preferência ao navegar. v2 só aparece
  // se o backend responder ao probe (feature flag KNOWLEDGE_V2_ENABLED).
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "v1";
    return localStorage.getItem("kb_view") || "v1";
  });
  const [v2Available, setV2Available] = useState(false);
  const [v2Probing, setV2Probing] = useState(true);
  // Active + non-expired doc count, matching what the backend counts
  // when snapshotting `summary.doc_count`. Used by the diagnostic
  // panel to flag "Pode estar desatualizado" when the inventory
  // changed since the last generation.
  const activeDocCount = items.filter(
    (d) => d.active !== false && !d.expired_at,
  ).length;

  const load = async () => {
    setLoading(true);
    try {
      const [allRes, reviewRes] = await Promise.all([
        api.get("/knowledge"),
        api.get("/knowledge/review").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(allRes.data) ? allRes.data : []);
      setReview(Array.isArray(reviewRes.data) ? reviewRes.data : []);
    } catch (e) {
      onToast?.("Erro ao carregar conhecimento.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Feature-flag probe — evita mostrar a aba v2 quando o backend a tem
  // desligada. Um GET raso em /knowledge/v2 responde 503 se desabilitada.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get("/knowledge/v2");
        if (!cancelled) setV2Available(true);
      } catch (err) {
        if (!cancelled) setV2Available(false);
      } finally {
        if (!cancelled) setV2Probing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist view selection
  useEffect(() => {
    try { localStorage.setItem("kb_view", view); } catch { /* localStorage unavailable */ }
  }, [view]);

  // ── Ações rápidas do painel de revisão ──
  const renew = async (id, days = 30) => {
    try {
      await api.post(`/knowledge/${id}/renew?ttl_days=${days}`);
      onToast?.(`Renovado por ${days} dias.`);
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao renovar.", "error");
    }
  };
  const deactivate = async (id) => {
    if (!window.confirm("Desativar esse conhecimento? O Agentus para de usá-lo imediatamente.")) return;
    try {
      await api.post(`/knowledge/${id}/toggle_active?active=false`);
      onToast?.("Desativado.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao desativar.", "error");
    }
  };
  const activate = async (id) => {
    try {
      await api.post(`/knowledge/${id}/toggle_active?active=true`);
      onToast?.("Reativado.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao reativar.", "error");
    }
  };

  const save = async () => {
    if (!editing.title?.trim() || !editing.content?.trim()) {
      onToast?.("Título e conteúdo obrigatórios.", "error");
      return;
    }
    setSaving(true);
    try {
      // v0.97-d — resolve preset → ISO via backend (validação central).
      let resolvedExpiresAt = editing.expires_at || null;
      const preset = editing.expires_preset;
      // v0.98 hotfix Fev/2026: `never` deve forçar `expires_at=null`
      // TAMBÉM em edição — antes só respeitava na criação, deixando
      // docs "permanentes" com o expires_at antigo, causando o bug de
      // aparecer no painel "Pra revisar" com "EXPIRA EM 3D" mesmo depois
      // do operador ter escolhido explicitamente "Não expira".
      if (preset === "never") {
        resolvedExpiresAt = null;
      } else if (preset && preset !== "never" && !editing.id) {
        // Presets com prazo só resolvem na CRIAÇÃO — em edição preservamos
        // o expires_at existente a não ser que o operador escolha `never`.
        try {
          const r = await api.post("/tenant/expiry-preset/compute",
                                    { preset });
          resolvedExpiresAt = r.data?.expires_at || null;
        } catch {
          onToast?.("Falha ao calcular data de expiração.", "error");
          setSaving(false);
          return;
        }
      }
      const payload = {
        title: editing.title.trim(),
        content: editing.content.trim(),
        category: editing.category || "policy",
        tags: editing.tags || [],
        active: editing.active !== false,
        expires_at: resolvedExpiresAt,
      };
      if (editing.id) {
        await api.put(`/knowledge/${editing.id}`, payload);
        onToast?.("Documento atualizado.");
      } else {
        await api.post("/knowledge", payload);
        onToast?.("Documento criado.");
      }
      setEditing(null);
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este documento?")) return;
    try {
      await api.delete(`/knowledge/${id}`);
      onToast?.("Removido.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao remover.", "error");
    }
  };

  // Upload PDF — multipart to /api/knowledge/upload. The backend extracts
  // the text, runs the qualitative-leak validator and persists the doc.
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      onToast?.("Por enquanto aceitamos só PDF.", "error");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/knowledge/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onToast?.("PDF processado e adicionado ao Cérebro.");
      await load();
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao subir PDF.", "error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  // ── v1/v2 toggle (aparece só quando a v2 está habilitada no backend) ──
  const Toggle = v2Available ? (
    <div
      className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200"
      data-testid="kb-view-toggle"
      role="tablist"
    >
      <button
        type="button"
        role="tab"
        aria-selected={view === "v1"}
        onClick={() => setView("v1")}
        data-testid="kb-view-v1"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
          view === "v1"
            ? "bg-white text-slate-900 shadow-sm border border-slate-200"
            : "text-slate-600 hover:text-slate-900"
        }`}
      >
        <Sparkle size={11} weight="bold" />
        Cultura &amp; FAQ
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={view === "v2"}
        onClick={() => setView("v2")}
        data-testid="kb-view-v2"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium transition-all ${
          view === "v2"
            ? "bg-white text-slate-900 shadow-sm border border-slate-200"
            : "text-slate-600 hover:text-slate-900"
        }`}
      >
        <Books size={11} weight="bold" />
        Documentos &amp; PDFs
        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-900 text-white text-[9px] tracking-wider">
          RAG
        </span>
      </button>
    </div>
  ) : null;

  // Modo v2 — delega ao painel v2 completo
  if (view === "v2" && v2Available) {
    return (
      <div className="space-y-6" data-testid="knowledge-tab">
        <div className="flex items-center justify-between">
          {Toggle}
          <div className="text-[11px] text-slate-400">
            Base v2 · Pipeline RAG com OCR, chunking e citação por página
          </div>
        </div>
        <KnowledgeV2Panel onToast={onToast} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-tab">
      {Toggle && (
        <div className="flex items-center justify-between">
          {Toggle}
          <div className="text-[11px] text-slate-400">
            Base v1 · Documentos curtos de cultura, política e FAQ
          </div>
        </div>
      )}
      <KnowledgeDiagnostic
        onToast={onToast}
        currentDocCount={activeDocCount}
        onUseRecommendation={(draft) => {
          // Iter-71.8 — pre-fill the existing editor with the LLM
          // draft. Operator can still edit anything before saving via
          // the normal POST /api/knowledge flow.
          setEditing({
            ...empty,
            title: draft.title || "",
            content: draft.content || "",
            category: draft.category || "policy",
          });
          // Modal opens centered (fixed inset-0). Drop the cursor on
          // the title input so the operator can refine the title
          // immediately without an extra click.
          setTimeout(() => {
            document
              .querySelector('[data-testid="knowledge-modal-title"]')
              ?.focus();
          }, 80);
        }}
      />

      <div className="flex items-center justify-between">
        <p className="text-[12px] text-slate-500">
          {items.length} doc{items.length === 1 ? "" : "s"} de cultura, políticas e FAQ.
        </p>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="knowledge-pdf-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid="knowledge-upload-pdf"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 hover:border-slate-900 text-slate-700 text-[13px] disabled:opacity-50"
          >
            {uploading ? (
              <CircleNotch size={12} className="animate-spin" />
            ) : (
              <FilePdf size={12} weight="bold" />
            )}
            {uploading ? "Processando…" : "Subir PDF"}
          </button>
          <button
            type="button"
            data-testid="knowledge-add"
            onClick={() => setEditing({ ...empty })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px]"
          >
            <Plus size={12} weight="bold" /> Novo documento
          </button>
        </div>
      </div>

      {/* ── v0.95: Painel de revisão — só aparece se há docs vencidos
         ou expirando em até 7d. Operador resolve em 1-clique.
         v0.98 hotfix: redesign monocromático (Apple premium). ── */}
      {review.length > 0 && (
        <div
          className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden"
          data-testid="knowledge-review-panel"
        >
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
              <Clock size={13} weight="fill" />
            </div>
            <h4 className="text-[13px] font-semibold text-slate-900 tracking-tight">
              Pra revisar
              <span className="text-slate-500 font-normal">
                {" "}· {review.length} conhecimento{review.length === 1 ? "" : "s"} pode{review.length === 1 ? "" : "m"} estar vencido{review.length === 1 ? "" : "s"}
              </span>
            </h4>
          </div>
          <div className="divide-y divide-slate-100">
            {review.map((d) => {
              const status = expiryStatus(d);
              return (
                <div
                  key={d.id}
                  data-testid={`knowledge-review-row-${d.id}`}
                  className="px-5 py-3.5 flex items-start gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-medium text-slate-900 truncate">{d.title}</span>
                      {status && (
                        <span
                          className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
                            status.tone === "rose"
                              ? "bg-rose-50 text-rose-700 border-rose-200"
                              : "bg-slate-100 text-slate-700 border-slate-200"
                          }`}
                        >
                          {status.label}
                        </span>
                      )}
                    </div>
                    <p className="text-[11.5px] text-slate-500 mt-0.5 line-clamp-2">
                      {(d.content || "").slice(0, 200)}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => renew(d.id, 30)}
                      data-testid={`knowledge-review-renew-${d.id}`}
                      title="Renovar por mais 30 dias"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors"
                    >
                      <ArrowClockwise size={11} weight="bold" /> Ainda vale
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(d)}
                      data-testid={`knowledge-review-edit-${d.id}`}
                      title="Editar conteúdo"
                      className="p-1.5 rounded-full hover:bg-amber-100 text-amber-700"
                    >
                      <PencilSimple size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deactivate(d.id)}
                      data-testid={`knowledge-review-disable-${d.id}`}
                      title="Desativar — Agentus para de usar"
                      className="p-1.5 rounded-full hover:bg-rose-50 text-slate-400 hover:text-rose-600"
                    >
                      <EyeSlash size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── v0.99.12 · Fev/2026 ─────────────────────────────────────
         "Como abastecer o Cérebro" — reescrito de 6-bullet-list-tímida
         para 4-blocos-universais. Antes: parecia checklist burocrático
         setor-específico. Agora: convite emocional que vale pra qualquer
         negócio (oficina, academia, clínica, escola, condomínio…).
         Cada bloco tem uma ideia grande e 1 linha de exemplo curto
         (não uma lista telefônica). ─────────────────────────────── */}
      <div
        className="rounded-2xl border border-slate-200/70 bg-gradient-to-b from-slate-50/70 to-white overflow-hidden"
        data-testid="knowledge-how-to-feed"
      >
        <div className="px-5 py-4 border-b border-slate-100/80 flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
            <Sparkle size={11} weight="fill" />
          </div>
          <div className="min-w-0">
            <h4 className="text-[13px] font-semibold text-slate-900 tracking-tight leading-tight">
              O que abastecer aqui
            </h4>
            <p className="text-[11.5px] text-slate-500 leading-tight mt-0.5">
              Se você quer que sua IA saiba, mora aqui. Sem receita pronta — você decide o que é importante.
            </p>
          </div>
        </div>

        <div className="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4" data-testid="knowledge-feed-blocks">
          <FeedBlock
            icon={Storefront}
            title="O básico do negócio"
            body="Endereço, formas de pagamento aceitas, canais oficiais e informações que o cliente pergunta antes de fechar."
          />
          <FeedBlock
            icon={Scroll}
            title="As regras da casa"
            body="Política de cancelamento, garantia, reembolso, descontos especiais e fidelidade — o que a equipe segue sem pensar."
          />
          <FeedBlock
            icon={ChatCircleText}
            title="As perguntas que sempre te fazem"
            body="Aquilo que você responde toda hora no WhatsApp — a IA aprende uma vez e responde no automático pra sempre."
          />
          <FeedBlock
            icon={Fingerprint}
            title="A cultura da marca"
            body="Sobre a empresa, valores, diferenciais, jeito de falar. O que você nunca diz — e o que faz a IA soar como você."
          />
        </div>

        <div className="px-5 py-3 border-t border-slate-100/80 bg-slate-50/50">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Preço, horário e serviço têm aba própria — se você mencionar aqui, o AgentUS ignora
            e usa sempre o cadastro oficial das outras abas. Isso mantém o Cérebro sem informação obsoleta.
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-6 py-10 rounded-2xl border border-dashed border-slate-200 text-center">
          <p className="text-sm text-slate-500">Nenhum documento cadastrado.</p>
        </div>
      ) : (
        <div className="border border-slate-100 rounded-2xl overflow-hidden divide-y divide-slate-100">
          {items.map((d) => {
            const warnings = d.warnings || [];
            const status = expiryStatus(d);
            const inactive = d.active === false;
            return (
              <div
                key={d.id}
                data-testid={`knowledge-row-${d.id}`}
                className={`px-5 py-4 flex items-start gap-4 hover:bg-slate-50 ${
                  inactive ? "opacity-60" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900">{d.title}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 border border-slate-200 rounded-full px-1.5 py-0.5">
                      {CATEGORIES.find((c) => c.value === d.category)?.label || d.category}
                    </span>
                    {status && (
                      <span
                        className={`text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border ${
                          status.tone === "rose"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : status.tone === "amber"
                            ? "bg-amber-50 text-amber-800 border-amber-200"
                            : "bg-slate-50 text-slate-500 border-slate-200"
                        }`}
                      >
                        {status.label}
                      </span>
                    )}
                    {inactive && (
                      <span
                        className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 border bg-slate-100 text-slate-500 border-slate-200"
                        data-testid={`knowledge-inactive-${d.id}`}
                      >
                        Inativo
                      </span>
                    )}
                    {warnings.length > 0 && (
                      <span
                        title={warnings.map((w) => w.label || w.code).join(", ")}
                        className="inline-flex items-center gap-1 text-[10px] text-amber-700 border border-amber-200 bg-amber-50 rounded-full px-1.5 py-0.5"
                      >
                        <Warning size={10} weight="bold" />
                        {warnings.length} alerta{warnings.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-500 mt-1 line-clamp-2">{d.content}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {inactive ? (
                    <button
                      type="button"
                      onClick={() => activate(d.id)}
                      data-testid={`knowledge-activate-${d.id}`}
                      title="Reativar — Agentus volta a usar"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                    >
                      <ArrowClockwise size={11} weight="bold" /> Reativar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => deactivate(d.id)}
                      data-testid={`knowledge-deactivate-${d.id}`}
                      title="Desativar — Agentus para de usar"
                      className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-amber-600"
                    >
                      <EyeSlash size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setEditing(d)}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                  >
                    <PencilSimple size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(d.id)}
                    className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-rose-600"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-6" data-testid="knowledge-modal">
          <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {editing.id ? "Editar documento" : "Novo documento"}
              </h3>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="p-1 rounded-full hover:bg-slate-100 text-slate-500"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <input
                type="text"
                placeholder="Título (ex.: Política de garantia)"
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
                data-testid="knowledge-modal-title"
              />
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>

              {/* v0.97-d — Preset de expiração. `expires_preset` é resolvido
                   no save() para o campo `expires_at` (ISO datetime). */}
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  Expira em
                </label>
                <select
                  value={editing.expires_preset || "never"}
                  onChange={(e) => setEditing({ ...editing, expires_preset: e.target.value })}
                  data-testid="knowledge-modal-expires-preset"
                  className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
                >
                  <option value="never">Não expira (permanente)</option>
                  <option value="2h">2 horas</option>
                  <option value="12h">12 horas</option>
                  <option value="today">Fim do dia (hoje)</option>
                  <option value="tomorrow">Fim do dia (amanhã)</option>
                  <option value="7d">7 dias</option>
                  <option value="15d">15 dias</option>
                  <option value="30d">30 dias</option>
                  <option value="90d">90 dias</option>
                  <option value="180d">180 dias</option>
                  <option value="1y">1 ano</option>
                </select>
                <p className="text-[10.5px] text-slate-400 mt-1">
                  Use expiração para informação temporária (promoção do mês, plantão do fim de semana).
                </p>
              </div>
              <textarea
                placeholder="Conteúdo (apenas qualitativo: políticas, cultura, procedimentos)"
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                rows={8}
                className="w-full px-3 py-2 rounded-2xl border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none resize-y"
                data-testid="knowledge-modal-content"
              />
              <div className="flex items-start gap-2 px-3.5 py-2.5 rounded-xl bg-slate-50/70 border border-slate-100">
                <Info size={13} weight="regular" className="text-slate-400 shrink-0 mt-[2px]" />
                <p className="text-[11.5px] text-slate-500 leading-[1.55]">
                  Evite repetir <span className="text-slate-700">preço, horário, serviço ou oferta</span> aqui — esses dados moram nas abas próprias e o Agentus usa elas como fonte oficial. Foque em <span className="text-slate-700">endereço e localização, setores internos + telefones diretos (peças, funilaria, pós-venda…), política, cultura, garantia, FAQ e procedimento</span>.
                </p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="px-4 py-2 rounded-full text-slate-600 hover:bg-slate-100 text-[13px]"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="knowledge-modal-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] disabled:opacity-50"
              >
                <FloppyDisk size={12} weight="bold" />
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * FeedBlock — Bloco visual universal usado na seção "O que abastecer aqui".
 *
 * v0.99.12 · Fev/2026 — Cartão emocional que substitui a antiga lista
 * de 6 bullets fria. Usa ícone Phosphor `duotone` num container arredondado
 * (linguagem visual do resto do app), título curto e body de 1-2 linhas.
 * Não é setor-específico: fala pra oficina, academia, clínica, escola…
 */
function FeedBlock({ icon: Icon, title, body }) {
  return (
    <div
      data-testid={`knowledge-feed-block-${title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-")}`}
      className="rounded-xl border border-slate-200/80 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 ring-1 ring-slate-200/70">
          <Icon size={17} weight="duotone" className="text-slate-800" />
        </div>
        <div className="min-w-0 pt-0.5">
          <div className="text-[13px] font-semibold text-slate-900 tracking-tight leading-tight">
            {title}
          </div>
          <p className="text-[12px] text-slate-500 mt-1 leading-[1.55]">
            {body}
          </p>
        </div>
      </div>
    </div>
  );
}
